import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUILDINGS } from "@/data/content";
import { InteractiveMap } from "@/components/site/InteractiveMap";
import { AnnotatedImage } from "@/components/site/AnnotatedImage";
import { ImagePlaceholder } from "@/components/site/ImagePlaceholder";
import { MaskLine, Reveal } from "@/components/site/Reveal";

const Hero = () => {
  const { t } = useLanguage();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section ref={ref} className="relative pt-24 md:pt-36">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.p
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          data-testid="hero-overline"
          className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-ink/50"
        >
          <span className="h-px w-10 bg-lagoon" />
          {t.hero.overline}
        </motion.p>
      </div>

      <motion.div
        initial={reduce ? false : { opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="mt-8 md:mt-12"
      >
        <motion.div style={{ y }} className="relative">
          <div className="relative aspect-[16/11] w-full overflow-hidden md:aspect-[21/9]">
            <img
              src="/images/hero-01.webp"
              alt="Fafapiti Lodge — vue aérienne du domaine"
              data-testid="img-photo-hero-01"
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-b from-transparent via-white/60 to-white" />
          </div>
          <span className="absolute right-5 top-4 bg-white/80 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60 backdrop-blur-md md:right-10 md:top-6">
            {t.hero.coords}
          </span>
        </motion.div>
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
        <h1 className="-mt-[clamp(2.4rem,7.5vw,6.4rem)] font-serif font-medium leading-[0.95] tracking-tight text-ink">
          <span className="block text-[clamp(4rem,13vw,11rem)]">
            <MaskLine delay={0.75}>{t.hero.line1}</MaskLine>
          </span>
          <span className="block text-[clamp(4rem,13vw,11rem)]">
            <MaskLine delay={0.9}>
              <span className="font-normal italic text-lagoon">{t.hero.line2}</span>
            </MaskLine>
          </span>
          <span className="block text-[clamp(1.6rem,4.5vw,3.5rem)] font-light italic text-ink/60">
            <MaskLine delay={1.1}>— {t.hero.line3}</MaskLine>
          </span>
        </h1>

        <div className="mt-12 flex flex-col gap-10 md:mt-16 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-sub"
            className="max-w-md text-base leading-relaxed text-ink/60 md:text-lg"
          >
            {t.hero.sub}
          </motion.p>
          <motion.a
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            data-testid="hero-cta"
            href="#le-bien"
            className="group inline-flex w-fit items-center gap-3 border border-ink px-7 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
          >
            {t.hero.cta}
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
          </motion.a>
        </div>
      </div>
    </section>
  );
};

const ChapterHeading = ({ kicker, title }) => (
  <Reveal>
    <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-lagoon">
      <span className="h-px w-10 bg-lagoon" />
      {kicker}
    </p>
    <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl lg:text-6xl">
      {title}
    </h2>
  </Reveal>
);

const ChapterProperty = () => {
  const { t } = useLanguage();
  const c = t.chapters.one;
  return (
    <section id="le-bien" className="scroll-mt-28 bg-sand py-24 md:py-40">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <ChapterHeading kicker={c.kicker} title={c.title} />

        <div className="mt-12 grid gap-12 md:mt-20 md:grid-cols-12">
          <Reveal delay={0.1} className="md:col-span-4 md:col-start-2">
            <p className="font-serif text-2xl leading-snug text-ink md:text-[1.75rem]">
              {c.text1}
            </p>
          </Reveal>
          <Reveal delay={0.2} className="md:col-span-7 md:col-start-6">
            <div className="relative aspect-[4/3] w-full overflow-hidden border border-neutral-200">
              <img
                src="/images/terrain-lagon.webp"
                alt="Vue aérienne du lagon de Fakarava"
                data-testid="img-terrain-01"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid items-stretch gap-10 md:mt-24 md:grid-cols-12">
          <Reveal className="flex flex-col justify-center md:order-2 md:col-span-4">
            <p className="text-base leading-relaxed text-ink/60 md:text-lg">
              {c.location}
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:order-1 md:col-span-8">
            <div className="flex h-full flex-col border border-neutral-200">
              <div className="flex items-center justify-between gap-4 border-b border-neutral-200 bg-white px-4 py-3 md:px-6">
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/50">
                  {c.mapTitle}
                </span>
                <a
                  href="https://www.google.com/maps/place/Fafapiti+Lodge+Fakarava/@-16.1795709,-145.5709941,15z"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="chapter-map-link"
                  className="inline-flex shrink-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-lagoon transition-colors duration-300 hover:text-nature"
                >
                  {c.mapLink}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
              <iframe
                title="Fafapiti Lodge Fakarava — Google Maps"
                data-testid="chapter-map-embed"
                src="https://maps.google.com/maps?q=-16.1795709,-145.5709941&t=k&z=13&output=embed"
                loading="lazy"
                className="h-[240px] w-full md:h-full md:min-h-[280px]"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="mt-12 md:mt-16">
          <div
            data-testid="land-specs"
            className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-3"
          >
            <div className="bg-white p-6 md:p-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/40">
                {c.landSurfaceLabel}
              </p>
              <p
                data-testid="land-surface"
                className="mt-3 font-serif text-3xl text-ink md:text-4xl"
              >
                {c.landSurface || t.building.toComplete}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-ink/60">
                {c.landBreakdown}
              </p>
              <button
                data-testid="land-info-link"
                onClick={() =>
                  document
                    .getElementById("informations")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="group mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-lagoon transition-colors duration-300 hover:text-nature"
              >
                {c.landLink}
                <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
              </button>
            </div>
            <div className="bg-white p-6 md:p-8 sm:col-span-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/40">
                {c.featuresLabel}
              </p>
              <ul className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
                {c.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-2.5 text-sm text-ink/70 md:text-base"
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-lagoon" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-5">
          <div className="relative w-full overflow-hidden border border-neutral-200">
            <img
              src="/images/terrain-aerien.webp"
              alt="Vue de dessus du domaine — les 5 bâtiments"
              data-testid="img-terrain-aerien"
              loading="lazy"
              className="h-auto w-full"
            />
          </div>
        </Reveal>

      </div>
    </section>
  );
};

const ChapterMap = () => {
  const { t } = useLanguage();
  return (
    <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-40">
      <ChapterHeading kicker={t.map.chapterKicker} title={t.map.title} />
      <Reveal delay={0.1}>
        <p className="mt-4 max-w-md text-base text-ink/60">{t.map.hint}</p>
      </Reveal>
      <div className="mt-10 md:mt-14">
        <InteractiveMap />
      </div>
    </section>
  );
};

const WATER_POINTS = [
  { x: 77.4, y: 18.6 },
  { x: 57.2, y: 24.8 },
  { x: 67.6, y: 44.5 },
  { x: 56, y: 53.4 },
  { x: 77.4, y: 64.7 },
];
const SOLAR_POINT = [{ x: 59.5, y: 67 }];

const InfoText = ({ data, testId }) => (
  <div className="mt-12 grid items-start gap-10 md:mt-16 md:grid-cols-12 md:items-stretch">
    <Reveal className="md:col-span-5">
      <h3 className="font-serif text-3xl tracking-tight text-ink md:text-4xl">
        {data.title}
      </h3>
      <div data-testid={`${testId}-text`} className="mt-4 space-y-3 text-base leading-relaxed text-ink/60">
        {data.text.split("\n").map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    </Reveal>
    <Reveal delay={0.1} className="md:col-span-7">
      <div className={`grid gap-4 md:h-full ${data.photos.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
        {data.photos.map((label) =>
          label.startsWith("/") ? (
            <div
              key={label}
              className="relative min-h-[240px] w-full overflow-hidden border border-neutral-200 bg-neutral-100 md:h-full"
            >
              <img
                src={label}
                alt={data.title}
                data-testid={`${testId}-photo`}
                loading="lazy"
                className={`absolute inset-0 h-full w-full ${
                  data.photos.length === 1 ? "object-contain" : "object-cover"
                }`}
              />
            </div>
          ) : (
            <ImagePlaceholder key={label} label={label} ratio="aspect-[16/9]" />
          )
        )}
      </div>
    </Reveal>
  </div>
);

const ChapterInfo = () => {
  const { t } = useLanguage();
  const c = t.info;
  return (
    <section id="informations" className="scroll-mt-28 bg-sand py-24 md:py-40">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <ChapterHeading kicker={c.kicker} title={c.title} />

        <Reveal className="mt-12 md:mt-16">
          <h3 className="font-serif text-3xl tracking-tight text-ink md:text-4xl">
            {c.cadastre.title}
          </h3>
          <div className="mt-8 grid items-stretch gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="mx-auto max-w-xl border border-neutral-200 bg-white p-3 md:p-4">
                <img
                  src="/images/cadastre.webp"
                  alt={c.cadastre.title}
                  data-testid="cadastre-img"
                  loading="lazy"
                  className="h-auto w-full"
                />
              </div>
              <ul className="mt-5 flex flex-wrap justify-center gap-x-8 gap-y-2">
                {c.cadastre.legend.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-center gap-2.5 text-sm text-ink/70"
                  >
                    <span
                      className="h-3 w-3 shrink-0 border border-ink/20"
                      style={{ backgroundColor: item.color }}
                    />
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-5">
              <div className="flex h-full flex-col border border-ink/10 bg-white p-6 md:p-8" data-testid="buildable-box">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-lagoon">
                  {c.cadastre.buildable.title}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">
                  {c.cadastre.buildable.text}
                </p>
                <div className="mt-5 grid flex-1 grid-cols-2 gap-4 [grid-auto-rows:1fr]">
                  {c.cadastre.buildable.photos.map((src, i) =>
                    src.startsWith("/") ? (
                      <div
                        key={src}
                        className={`relative min-h-[140px] w-full overflow-hidden border border-neutral-200 ${
                          i === 0 ? "col-span-2 min-h-[200px]" : ""
                        }`}
                      >
                        <img
                          src={src}
                          alt={`${c.cadastre.buildable.title} — ${i + 1}`}
                          data-testid={`ck55-photo-${i + 1}`}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <ImagePlaceholder
                        key={src}
                        label={src}
                        ratio="aspect-[4/3]"
                        className={i === 0 ? "col-span-2" : ""}
                      />
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 md:mt-16">
          {c.docs.map((d, i) => (
            <Reveal key={d.title} delay={0.08 * i} className="bg-white p-8 md:p-12">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/40">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-3 font-serif text-2xl tracking-tight text-ink md:text-3xl">
                {d.title}
              </h3>
              <div data-testid={`info-doc-${i + 1}`} className="mt-3 space-y-1.5 text-sm leading-relaxed text-ink/60">
                {d.text.split("\n").map((line) => (
                  <p key={line} className="font-mono">{line}</p>
                ))}
              </div>
            </Reveal>
          ))}
        </div>

        <InfoText data={c.water} testId="info-water" />

        <Reveal className="mt-10 md:mt-14">
          <AnnotatedImage
            src="/images/terrain-aerien.webp"
            alt={`${c.water.title} — ${c.elec.title}`}
            testId="info-diagram"
            groups={[
              { label: c.water.pointLabel, position: "top", points: WATER_POINTS },
              {
                label: c.elec.pointLabel,
                position: "bottom",
                points: SOLAR_POINT,
                color: "#F59E0B",
                big: true,
              },
            ]}
          />
        </Reveal>

        <InfoText data={c.elec} testId="info-elec" />
      </div>
    </section>
  );
};

const ChapterBuildings = () => {
  const { lang, t } = useLanguage();
  const c = t.chapters.two;
  return (
    <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-40">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <ChapterHeading kicker={c.kicker} title={c.title} />
        <Reveal delay={0.15}>
          <p className="max-w-xs text-sm leading-relaxed text-ink/50">{c.hint}</p>
        </Reveal>
      </div>
      <div className="mt-12 border-t border-ink/10 md:mt-20">
        {BUILDINGS.map((b, i) => (
          <Reveal key={b.slug} delay={0.05 * i}>
            <Link
              to={`/batiment/${b.slug}`}
              data-testid={`home-building-${b.slug}`}
              className="group flex items-center justify-between gap-4 border-b border-ink/10 py-7 transition-colors duration-300 hover:bg-sand md:py-10"
            >
              <div className="flex items-baseline gap-5 md:gap-10">
                <span className="font-serif text-sm italic text-lagoon md:text-lg">
                  {b.num}
                </span>
                <span className="font-serif text-2xl tracking-tight text-ink transition-transform duration-500 group-hover:translate-x-2 sm:text-4xl md:text-6xl">
                  {b.name[lang]}
                </span>
              </div>
              <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/40 transition-colors duration-300 group-hover:text-lagoon">
                <span className="hidden sm:inline">{c.visit}</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <ChapterProperty />
      <ChapterMap />
      <ChapterInfo />
      <ChapterBuildings />
    </div>
  );
}
